import React, { useState, useEffect, useContext, useCallback } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist-min';
import { AdminCompanyContext } from '../../AdminApp';
import { getAdminTokenUsage } from '../../adminApi';
import { useCompanyNames, resolveCompanyName } from '../../hooks/useCompanyNames';
import { useUserNames, resolveUserName } from '../../hooks/useUserNames';

const Plot = createPlotlyComponent(Plotly);

const PAGE_SIZE = 50;
const PERIODS = [
  { label: '7d',       days: 7   },
  { label: '30d',      days: 30  },
  { label: 'All time', days: null },
];

// '' means all sources: the API treats an absent `source` as no filter.
const SOURCES = [
  { label: 'All sources', value: '' },
  { label: 'Chat',        value: 'chat' },
  { label: 'Describe',    value: 'describe' },
];

// Rows recorded before the source column existed carry no source and are chat.
function rowSource(row) {
  return row.source || 'chat';
}

function SourceBadge({ source }) {
  return (
    <span className={`admin-source-badge ${source}`}>
      {source === 'describe' ? 'Describe' : 'Chat'}
    </span>
  );
}

function sinceFromDays(days) {
  if (!days) return undefined;
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function shortId(id) {
  if (!id) return '—';
  return id.length > 14 ? id.slice(0, 6) + '…' + id.slice(-4) : id;
}

function exportCsv(rows) {
  const headers = ['Date','Source','Company','User','Query','Prompt Tokens','Completion Tokens','Total Tokens','Cost USD','Model','Response Time ms'];
  const lines = rows.map(r => [
    r.recorded_at,
    rowSource(r),
    r.company_id,
    r.user_id,
    `"${(r.query_preview || '').replace(/"/g, '""')}"`,
    r.prompt_tokens,
    r.completion_tokens,
    r.total_tokens,
    r.estimated_cost_usd,
    r.model,
    r.response_time_ms,
  ].join(','));
  const csv = [headers.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `token-usage-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminTokenUsage() {
  const { selectedCompany } = useContext(AdminCompanyContext);
  const companyNames = useCompanyNames();
  const userNames = useUserNames();

  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [period, setPeriod]     = useState(1);        // index into PERIODS
  const [company, setCompany]   = useState('');
  const [source, setSource]     = useState('');
  const [page, setPage]         = useState(0);

  // Pre-populate company filter from global context on mount
  useEffect(() => {
    if (selectedCompany) setCompany(selectedCompany);
  }, [selectedCompany]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const since = sinceFromDays(PERIODS[period].days);
      const data  = await getAdminTokenUsage({
        company_id: company || undefined,
        source: source || undefined,
        since,
        limit: 200,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(0);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load token usage');
    } finally {
      setLoading(false);
    }
  }, [period, company, source]);

  useEffect(() => { load(); }, [load]);

  // ── Aggregates ──────────────────────────────────────────────────────────────
  const totalTokens  = items.reduce((s, r) => s + (r.total_tokens || 0), 0);
  const totalCost    = items.reduce((s, r) => s + (r.estimated_cost_usd || 0), 0);
  const avgTokens    = items.length ? Math.round(totalTokens / items.length) : 0;
  const avgRespTime  = items.length
    ? Math.round(items.reduce((s, r) => s + (r.response_time_ms || 0), 0) / items.length)
    : 0;

  // ── Bar chart: tokens per company ───────────────────────────────────────────
  const companyMap = {};
  items.forEach(r => {
    const c = r.company_id || 'unknown';
    if (!companyMap[c]) companyMap[c] = { prompt: 0, completion: 0 };
    companyMap[c].prompt     += r.prompt_tokens     || 0;
    companyMap[c].completion += r.completion_tokens || 0;
  });
  const companies   = Object.keys(companyMap);
  const shortLabels = companies.map(c => c.length > 10 ? c.slice(0, 6) + '…' + c.slice(-4) : c);

  const chartData = [
    {
      x: shortLabels,
      y: companies.map(c => companyMap[c].prompt),
      type: 'bar',
      name: 'Prompt',
      marker: { color: '#2563eb' },
      hovertext: companies.map(c => `${c}\nPrompt: ${companyMap[c].prompt.toLocaleString()}`),
      hoverinfo: 'text',
    },
    {
      x: shortLabels,
      y: companies.map(c => companyMap[c].completion),
      type: 'bar',
      name: 'Completion',
      marker: { color: '#10b981' },
      hovertext: companies.map(c => `${c}\nCompletion: ${companyMap[c].completion.toLocaleString()}`),
      hoverinfo: 'text',
    },
  ];

  const chartLayout = {
    barmode: 'stack',
    autosize: true,
    height: 260,
    margin: { t: 16, r: 16, b: 40, l: 60 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { family: 'Montserrat, sans-serif', size: 12, color: '#6b7280' },
    legend: { x: 0, y: 1.18, orientation: 'h' },
    yaxis: { title: 'Tokens', showgrid: true, gridcolor: '#f3f4f6' },
    xaxis: { showgrid: false },
  };

  // ── Pagination ───────────────────────────────────────────────────────────────
  const pageCount  = Math.ceil(items.length / PAGE_SIZE);
  const pageItems  = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div>
      {/* ── Header + filters ── */}
      <div className="admin-toolbar">
        <h2 className="admin-section-title" style={{ margin: 0 }}>Token Usage</h2>
        <div className="admin-toolbar-controls">
          <div className="admin-period-btns">
            {PERIODS.map((p, i) => (
              <button
                key={p.label}
                className={`admin-period-btn${period === i ? ' active' : ''}`}
                onClick={() => setPeriod(i)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <input
            className="admin-company-input"
            placeholder="Filter by company ID…"
            aria-label="Filter by company ID"
            value={company}
            onChange={e => setCompany(e.target.value)}
          />
          <select
            className="admin-source-select"
            aria-label="Filter by source"
            value={source}
            onChange={e => setSource(e.target.value)}
          >
            {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button className="admin-export-btn" onClick={() => exportCsv(items)} disabled={items.length === 0}>
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-error-state">
          <p>{error}</p>
          <button onClick={() => { setLoading(true); load(); }}>Retry</button>
        </div>
      )}

      {/* ── Aggregate cards ── */}
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        {loading ? (
          [1,2,3,4].map(i => (
            <div key={i} className="stat-card">
              <div className="admin-skeleton admin-skeleton-label" />
              <div className="admin-skeleton admin-skeleton-value" />
            </div>
          ))
        ) : (
          <>
            <div className="stat-card">
              <div className="stat-card-label">Total Tokens</div>
              <div className="stat-card-value">{totalTokens.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Total Cost</div>
              <div className="stat-card-value">${totalCost.toFixed(2)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Avg Tokens / Query</div>
              <div className="stat-card-value">{avgTokens.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-label">Avg Response Time</div>
              <div className="stat-card-value">{avgRespTime.toLocaleString()} ms</div>
            </div>
          </>
        )}
      </div>

      {/* ── Bar chart ── */}
      {!loading && companies.length > 0 && (
        <div className="admin-chart-card">
          <div className="admin-chart-title">Tokens by Company</div>
          <Plot
            data={chartData}
            layout={chartLayout}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        </div>
      )}

      {/* ── History table ── */}
      {!loading && !error && (
        <>
          <div className="admin-table-meta">
            {items.length} record{items.length !== 1 ? 's' : ''}
            {total > items.length ? ` (showing first ${items.length} of ${total} — refine filters to see more)` : ''}
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Source</th>
                  <th>Company</th>
                  <th>User</th>
                  <th>Query</th>
                  <th style={{ textAlign: 'right' }}>Prompt T</th>
                  <th style={{ textAlign: 'right' }}>Completion T</th>
                  <th style={{ textAlign: 'right' }}>Cost</th>
                  <th>Model</th>
                  <th style={{ textAlign: 'right' }}>Resp. Time</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--admin-text-muted)' }}>No records found.</td></tr>
                ) : pageItems.map(r => (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.recorded_at)}</td>
                    <td><SourceBadge source={rowSource(r)} /></td>
                    <td title={r.company_id}>{resolveCompanyName(companyNames, r.company_id)}</td>
                    <td title={r.user_id}>{resolveUserName(userNames, r.user_id)}</td>
                    <td title={r.query_preview} style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.query_preview}
                    </td>
                    <td style={{ textAlign: 'right' }}>{(r.prompt_tokens || 0).toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{(r.completion_tokens || 0).toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>${(r.estimated_cost_usd || 0).toFixed(4)}</td>
                    <td style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{r.model || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.response_time_ms ? `${r.response_time_ms.toLocaleString()} ms` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="admin-pagination">
              <button onClick={() => setPage(p => p - 1)} disabled={page === 0}>← Prev</button>
              <span>Page {page + 1} of {pageCount}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= pageCount - 1}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
